import { c as _c } from "react/compiler-runtime";
import { Box, Button, Center, HStack, IconButton, Input, InputGroup, Text, VStack } from '@chakra-ui/react';
import { UserAvatar } from '@src/components/sco/Account/UserAvatar';
import { AdvisorsLoadMore } from '@src/components/sco/Advisors/shared/AdvisorsLoadMore';
import { ButtonLink } from '@src/components/sco/ButtonLink';
import { CloseIcon, SearchIcon } from '@src/components/sco/icons';
import { useEffect, useMemo, useState } from 'react';
export type EngagementSearchListItem = {
  key: string;
  name: string;
  href: string;
  userId?: string | null;
  subtitle?: string | null;
  /** Extra strings included in client-side name search (company, etc.). */
  searchText?: string;
};
type EngagementSearchListProps = {
  items: EngagementSearchListItem[];
  maxItems?: number;
  searchPlaceholder?: string;
  emptyMessage?: string;
  noMatchMessage?: string;
  /**
   * When true, search is controlled by the parent and filtering happens server-side.
   * Parent must pass `search` / `onSearchChange` and already-filtered `items`.
   */
  serverSide?: boolean;
  search?: string;
  onSearchChange?: (value: string) => void;
  /** Server-side pagination (1-based). When pageCount > 1, prev/next controls render. */
  page?: number;
  pageCount?: number;
  itemCount?: number;
  onPageChange?: (page: number) => void;
  loading?: boolean;
};
const matchesSearch = (item: EngagementSearchListItem, query: string) => {
  if (!query) {
    return true;
  }
  const haystack = [item.name, item.subtitle, item.searchText].filter(Boolean).join(' ').toLowerCase();
  return haystack.includes(query);
};

/**
 * Searchable, paginated person list. Renders no page title or scroll container — mount it inside a
 * layout that owns both (e.g. `MyFoundersLayout` → `AdvisorsSubNavLayout`).
 */
export const EngagementSearchList = t0 => {
  const $ = _c(57);
  const {
    items,
    maxItems: t1,
    searchPlaceholder: t2,
    emptyMessage: t3,
    noMatchMessage: t4,
    serverSide: t5,
    search: controlledSearch,
    onSearchChange,
    page: t6,
    pageCount: t7,
    itemCount,
    onPageChange,
    loading: t8
  } = t0;
  const maxItems = t1 === undefined ? 100 : t1;
  const searchPlaceholder = t2 === undefined ? "Search by name..." : t2;
  const emptyMessage = t3 === undefined ? "No results yet." : t3;
  const noMatchMessage = t4 === undefined ? "No results match your search." : t4;
  const serverSide = t5 === undefined ? false : t5;
  const page = t6 === undefined ? 1 : t6;
  const pageCount = t7 === undefined ? 1 : t7;
  const loading = t8 === undefined ? false : t8;
  const [localSearch, setLocalSearch] = useState("");
  const search = serverSide ? controlledSearch ?? "" : localSearch;
  let t9;
  if ($[0] !== onSearchChange || $[1] !== serverSide) {
    t9 = value => {
      if (serverSide) {
        onSearchChange?.(value);
      } else {
        setLocalSearch(value);
      }
    };
    $[0] = onSearchChange;
    $[1] = serverSide;
    $[2] = t9;
  } else {
    t9 = $[2];
  }
  const setSearch = t9;
  let t10;
  if ($[3] !== search) {
    t10 = search.trim().toLowerCase();
    $[3] = search;
    $[4] = t10;
  } else {
    t10 = $[4];
  }
  const query = t10;
  const [visibleCount, setVisibleCount] = useState(maxItems);
  let t11;
  if ($[5] !== maxItems) {
    t11 = () => {
      setVisibleCount(maxItems);
    };
    $[5] = maxItems;
    $[6] = t11;
  } else {
    t11 = $[6];
  }
  let t12;
  if ($[7] !== maxItems || $[8] !== query) {
    t12 = [maxItems, query];
    $[7] = maxItems;
    $[8] = query;
    $[9] = t12;
  } else {
    t12 = $[9];
  }
  useEffect(t11, t12);
  let t13;
  bb0: {
    if (serverSide) {
      t13 = items;
      break bb0;
    }
    let t14;
    if ($[10] !== items || $[11] !== query || $[12] !== visibleCount) {
      let t15;
      if ($[14] !== query) {
        t15 = item => matchesSearch(item, query);
        $[14] = query;
        $[15] = t15;
      } else {
        t15 = $[15];
      }
      t14 = items.filter(t15).slice(0, visibleCount);
      $[10] = items;
      $[11] = query;
      $[12] = visibleCount;
      $[13] = t14;
    } else {
      t14 = $[13];
    }
    t13 = t14;
  }
  const visibleItems = t13;
  let t14;
  bb1: {
    if (serverSide) {
      t14 = itemCount ?? items.length;
      break bb1;
    }
    let t15;
    if ($[16] !== items || $[17] !== query) {
      let t16;
      if ($[19] !== query) {
        t16 = item_0 => matchesSearch(item_0, query);
        $[19] = query;
        $[20] = t16;
      } else {
        t16 = $[20];
      }
      t15 = items.filter(t16);
      $[16] = items;
      $[17] = query;
      $[18] = t15;
    } else {
      t15 = $[18];
    }
    t14 = t15.length;
  }
  const totalMatching = t14;
  const clientHasMore = !serverSide && totalMatching > visibleCount;
  const showPagination = serverSide && pageCount > 1 && !!onPageChange;
  const hasActiveSearch = !!query;
  let t15;
  bb2: {
    if (loading && items.length === 0) {
      t15 = "Loading...";
      break bb2;
    }
    if (items.length === 0 && !hasActiveSearch) {
      t15 = null;
      break bb2;
    }
    if (totalMatching === 0) {
      t15 = null;
      break bb2;
    }
    if (serverSide) {
      t15 = `Showing ${visibleItems.length}${totalMatching > visibleItems.length ? ` of ${totalMatching}` : ""} result${visibleItems.length === 1 ? "" : "s"}${pageCount > 1 ? ` · Page ${page} of ${pageCount}` : ""}`;
      break bb2;
    }
    t15 = `Showing ${visibleItems.length}${totalMatching > visibleItems.length ? ` of ${totalMatching}` : ""} result${visibleItems.length === 1 ? "" : "s"}`;
  }
  const statusText = t15;
  let t16;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <SearchIcon color="colorPalette.fg/60" />;
    $[21] = t16;
  } else {
    t16 = $[21];
  }
  let t17;
  if ($[22] !== search || $[23] !== setSearch) {
    t17 = search ? <IconButton aria-label="Clear search" size="xs" variant="ghost" onClick={() => setSearch("")}><CloseIcon boxSize={2} /></IconButton> : undefined;
    $[22] = search;
    $[23] = setSearch;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  let t18;
  if ($[25] !== setSearch) {
    t18 = e => setSearch(e.target.value);
    $[25] = setSearch;
    $[26] = t18;
  } else {
    t18 = $[26];
  }
  let t19;
  if ($[27] !== search || $[28] !== searchPlaceholder || $[29] !== t18) {
    t19 = <Input paddingLeft={10} placeholder={searchPlaceholder} size="md" type="text" value={search} onChange={t18} />;
    $[27] = search;
    $[28] = searchPlaceholder;
    $[29] = t18;
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  let t20;
  if ($[31] !== t17 || $[32] !== t19) {
    t20 = <InputGroup startElement={t16} endElement={t17}>{t19}</InputGroup>;
    $[31] = t17;
    $[32] = t19;
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  let t21;
  if ($[34] !== statusText) {
    t21 = statusText ? <Text color="colorPalette.fg/50" fontSize="xs">{statusText}</Text> : null;
    $[34] = statusText;
    $[35] = t21;
  } else {
    t21 = $[35];
  }
  let t22;
  if ($[36] !== emptyMessage || $[37] !== hasActiveSearch || $[38] !== items.length || $[39] !== noMatchMessage || $[40] !== visibleItems) {
    t22 = items.length === 0 && !hasActiveSearch ? <Center paddingY={10}><Text color="colorPalette.fg/50">{emptyMessage}</Text></Center> : visibleItems.length === 0 ? <Center paddingY={10}><Text color="colorPalette.fg/50">{noMatchMessage}</Text></Center> : <VStack align="stretch" gap={1} width="full">{visibleItems.map(_temp)}</VStack>;
    $[36] = emptyMessage;
    $[37] = hasActiveSearch;
    $[38] = items.length;
    $[39] = noMatchMessage;
    $[40] = visibleItems;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  let t23;
  if ($[42] !== loading || $[43] !== onPageChange || $[44] !== page || $[45] !== pageCount || $[46] !== showPagination) {
    t23 = showPagination && <HStack gap={4} justify="center" paddingTop={4}><Button disabled={page <= 1 || loading} size="sm" variant="outline" onClick={() => onPageChange?.(Math.max(1, page - 1))}>← Previous</Button><Text color="colorPalette.fg/60" fontSize="sm">Page {page} of {pageCount}</Text><Button disabled={page >= pageCount || loading} size="sm" variant="outline" onClick={() => onPageChange?.(Math.min(pageCount, page + 1))}>Next →</Button></HStack>;
    $[42] = loading;
    $[43] = onPageChange;
    $[44] = page;
    $[45] = pageCount;
    $[46] = showPagination;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] !== clientHasMore || $[49] !== maxItems) {
    t24 = clientHasMore && <AdvisorsLoadMore hasNextPage={true} onLoadMore={() => setVisibleCount(count => count + maxItems)} />;
    $[48] = clientHasMore;
    $[49] = maxItems;
    $[50] = t24;
  } else {
    t24 = $[50];
  }
  let t25;
  if ($[51] !== t20 || $[52] !== t21 || $[53] !== t22 || $[54] !== t23 || $[55] !== t24) {
    t25 = <VStack align="stretch" gap={4} maxWidth="3xl" width="full">{t20}{t21}{t22}{t23}{t24}</VStack>;
    $[51] = t20;
    $[52] = t21;
    $[53] = t22;
    $[54] = t23;
    $[55] = t24;
    $[56] = t25;
  } else {
    t25 = $[56];
  }
  return t25;
};
function _temp(item_1) {
  return <ButtonLink key={item_1.key} height="auto" href={item_1.href} justifyContent="flex-start" paddingX={3} paddingY={3} variant="ghost" width="full"><Box alignItems="center" as="span" display="flex" gap={3} minWidth={0} width="full"><UserAvatar avatarProps={{
        size: "sm"
      }} noLink={true} userId={item_1.userId || undefined} /><VStack align="stretch" as="span" flex={1} gap={0} minWidth={0}><Text fontWeight="medium" lineClamp={1} textAlign="left">{item_1.name}</Text>{item_1.subtitle && <Text color="colorPalette.fg/55" fontSize="sm" lineClamp={1} textAlign="left">{item_1.subtitle}</Text>}</VStack></Box></ButtonLink>;
}